/**
 * O que faz: regras de apresentação do painel "Leads para retomar hoje": grupos na ordem de urgência,
 *   nomes, cores e as frases mostradas no cartão e na lista.
 * Onde usa: CartaoRetomadaDashboard, RetomadaListaModal e os schemas de retomadaService.
 * Conexões: espelha os valores devolvidos pela migration 20261023150000_painel_retomada.sql.
 * Tabelas: nenhuma; funções puras, testadas em regras.test.ts.
 * Data: 22/10/2026.
 */

// Ordem de urgência: quem espera resposta vem antes de quem só parou de responder.
export const GRUPOS_RETOMADA = ['esperando_resposta', 'parou_ontem', 'parou_2_3_dias', 'parou_4_7_dias'] as const;
export type GrupoRetomada = typeof GRUPOS_RETOMADA[number];

export const SITUACOES_RETOMADA = ['pendente', 'retomado', 'lead_voltou'] as const;
export type SituacaoRetomada = typeof SITUACOES_RETOMADA[number];

// A cor nunca aparece sozinha: o nome do grupo sempre vai junto (acessibilidade).
export const APRESENTACAO_GRUPOS: Record<GrupoRetomada, {
  nome: string;
  descricao: string;
  classeCartao: string;
  classePonto: string;
}> = {
  esperando_resposta: {
    nome: 'Esperando resposta',
    descricao: 'O lead falou por último e a equipe ainda não respondeu.',
    classeCartao: 'bg-danger-subtle text-danger-text',
    classePonto: 'bg-danger-text',
  },
  parou_ontem: {
    nome: 'Pararam ontem',
    descricao: 'A equipe falou por último ontem e o lead não respondeu. Retomar hoje.',
    classeCartao: 'bg-warning-subtle text-warning-text',
    classePonto: 'bg-warning-text',
  },
  parou_2_3_dias: {
    nome: 'Pararam há 2–3 dias',
    descricao: 'Segunda tentativa: a conversa esfriou há poucos dias.',
    classeCartao: 'bg-info-subtle text-info-text',
    classePonto: 'bg-info-text',
  },
  parou_4_7_dias: {
    nome: 'Pararam há 4–7 dias',
    descricao: 'Última tentativa antes de o lead sair do painel.',
    classeCartao: 'bg-secondary text-muted-foreground',
    classePonto: 'bg-muted-foreground',
  },
};

/** Frase curta da lista: há quanto tempo parou e se a pessoa já conversou antes. */
export function descreverParada(item: { grupo: GrupoRetomada; dias_parado: number; ja_respondeu: boolean }): string {
  if (item.grupo === 'esperando_resposta') {
    return item.dias_parado === 1
      ? 'Mandou mensagem ontem e espera resposta'
      : `Mandou mensagem há ${item.dias_parado} dias e espera resposta`;
  }
  const quando = item.dias_parado === 1 ? 'Parou de responder ontem' : `Parou de responder há ${item.dias_parado} dias`;
  // Quem já conversou costuma ser mais fácil de retomar do que quem nunca respondeu.
  return `${quando} · ${item.ja_respondeu ? 'já conversou antes' : 'nunca respondeu'}`;
}

/** Selo do item; pendente não ganha selo para a lista ficar limpa. */
export function descreverSituacao(situacao: SituacaoRetomada): string | null {
  if (situacao === 'retomado') return 'Retomado hoje';
  if (situacao === 'lead_voltou') return 'Voltou a falar hoje';
  return null;
}

// Formatadores fixos no fuso do negócio: a equipe lê horários de São Paulo mesmo com o navegador em outro fuso.
const FORMATO_DIA_ISO = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });
const FORMATO_HORA = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
const FORMATO_DIA_MES = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
const UM_DIA_MS = 24 * 60 * 60 * 1000;

/**
 * Horário curto para a lista: "hoje às 10:15", "ontem às 23:30" ou "20/10 às 14:05".
 * Motivo de usar o dia da lista (vindo do banco) como referência: "hoje" e "ontem" batem com os
 * grupos calculados no servidor, mesmo se o relógio do computador estiver errado.
 * Data inválida devolve texto vazio para a tela simplesmente não mostrar o horário.
 */
export function formatarHorario(iso: string, diaReferencia: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  const hora = FORMATO_HORA.format(data);
  const diferencaDias = Math.round(
    (Date.parse(`${diaReferencia}T00:00:00Z`) - Date.parse(`${FORMATO_DIA_ISO.format(data)}T00:00:00Z`)) / UM_DIA_MS,
  );
  if (diferencaDias === 0) return `hoje às ${hora}`;
  if (diferencaDias === 1) return `ontem às ${hora}`;
  return `${FORMATO_DIA_MES.format(data)} às ${hora}`;
}

/** Linha de fiscalização do cartão: quantos dos leads de ontem foram retomados. */
export function resumirOntem(resultado: { total: number; retomados: number }): string {
  // Sem leads ontem, uma porcentagem seria divisão por zero e não diria nada útil.
  if (resultado.total === 0) return 'Ontem não havia leads para retomar.';
  const arredondada = Math.round((resultado.retomados * 100) / resultado.total);
  // Arredondar mente nos extremos: 199 de 200 viraria "100%" e 1 de 300 viraria "0%", como se todos
  // (ou ninguém) tivessem sido retomados. Quem retomou só alguns nunca mostra 0% nem 100%; 0 e todos continuam exatos.
  const parcial = resultado.retomados > 0 && resultado.retomados < resultado.total;
  const porcentagem = parcial ? Math.min(99, Math.max(1, arredondada)) : arredondada;
  const verbo = resultado.retomados === 1 ? 'foi retomado' : 'foram retomados';
  return `Ontem: ${resultado.retomados} de ${resultado.total} ${verbo} (${porcentagem}%).`;
}

export function totalPendentes(grupos: Array<{ pendentes: number }>): number {
  return grupos.reduce((soma, grupo) => soma + grupo.pendentes, 0);
}
